import {
  Button,
  Divider,
  Flex,
  Text,
  Form,
  CircleIcon,
  FormNumberInput,
  AlertTitle,
  Alert,
} from '@shared/components';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useItemAnalysisSearchParams } from '../../hooks';

export const PBufferConditionFormSchema = z
  .object({
    pBufferMinHardThreshold: z.number({ message: '' }),
    pBufferMaxHardThreshold: z.number({ message: '' }),
    pBufferMinMediumThreshold: z.number({ message: '' }),
    pBufferMaxMediumThreshold: z.number({ message: '' }),
    pBufferMinEasyThreshold: z.number({ message: '' }),
    pBufferMaxEasyThreshold: z.number({ message: '' }),
    errorMessage: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const {
      pBufferMinHardThreshold: hMin,
      pBufferMaxHardThreshold: hMax,
      pBufferMinMediumThreshold: mMin,
      pBufferMaxMediumThreshold: mMax,
      pBufferMinEasyThreshold: eMin,
      pBufferMaxEasyThreshold: eMax,
    } = data;

    const toInt = (v: number) => Math.round(v * 100);

    const hMinI = toInt(hMin);
    const hMaxI = toInt(hMax);
    const mMinI = toInt(mMin);
    const mMaxI = toInt(mMax);
    const eMinI = toInt(eMin);
    const eMaxI = toInt(eMax);

    const addCommonIssues = () => {
      const paths: (keyof typeof data | 'errorMessage')[] = [
        'errorMessage',
        'pBufferMaxHardThreshold',
        'pBufferMinMediumThreshold',
        'pBufferMaxMediumThreshold',
        'pBufferMinEasyThreshold',
      ];

      for (const path of paths) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '',
          path: [path],
        });
      }
    };

    if (!(hMinI < hMaxI && mMinI < mMaxI && eMinI < eMaxI)) {
      addCommonIssues();
      return;
    }

    if (hMinI !== 0 || eMaxI !== 100) {
      addCommonIssues();
      return;
    }

    if (mMinI - hMaxI !== 1 || eMinI - mMaxI !== 1) {
      addCommonIssues();
    }
  });

export type PBufferConditionFormValues = z.infer<typeof PBufferConditionFormSchema>;

export default function PBufferConditionSection() {
  const [
    { pBufferEasyThreshold, pBufferHardThreshold, pBufferMediumThreshold },
    { changePBufferThresholds },
  ] = useItemAnalysisSearchParams();

  const formContext = useForm<PBufferConditionFormValues>({
    defaultValues: {
      pBufferMinHardThreshold: 0.0,
      pBufferMaxHardThreshold: pBufferHardThreshold ?? 0.4,
      pBufferMinMediumThreshold: Number(((pBufferHardThreshold ?? 0.4) + 0.01).toFixed(2)),
      pBufferMaxMediumThreshold: pBufferMediumThreshold ?? 0.7,
      pBufferMinEasyThreshold: Number(((pBufferMediumThreshold ?? 0.7) + 0.01).toFixed(2)),
      pBufferMaxEasyThreshold: pBufferEasyThreshold ?? 1.0,
    },
    resolver: zodResolver(PBufferConditionFormSchema),
  });

  const { formState } = formContext;

  const isError =
    formState.errors.pBufferMaxHardThreshold !== undefined ||
    formState.errors.pBufferMinMediumThreshold !== undefined ||
    formState.errors.pBufferMaxMediumThreshold !== undefined ||
    formState.errors.pBufferMinEasyThreshold !== undefined;

  const onSubmit = (data: PBufferConditionFormValues) => {
    const { pBufferMaxHardThreshold, pBufferMaxMediumThreshold } = data;
    changePBufferThresholds({
      pBufferHardThreshold: pBufferMaxHardThreshold ?? 0.4,
      pBufferMediumThreshold: pBufferMaxMediumThreshold ?? 0.7,
      pBufferEasyThreshold: 1,
    });
  };

  const handleReset = () => {
    changePBufferThresholds({
      pBufferHardThreshold: 0.4,
      pBufferMediumThreshold: 0.7,
      pBufferEasyThreshold: 1,
    });
    formContext.reset({
      pBufferMinHardThreshold: 0.0,
      pBufferMaxHardThreshold: 0.4,
      pBufferMinMediumThreshold: 0.41,
      pBufferMaxMediumThreshold: 0.7,
      pBufferMinEasyThreshold: 0.71,
      pBufferMaxEasyThreshold: 1,
    });
  };

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
      {formContext.formState.errors.errorMessage || isError ? (
        <Alert
          severity='error'
          sx={{
            alignItems: 'flex-start',
            '& .MuiAlert-message': {
              overflow: 'unset',
            },
          }}
        >
          <AlertTitle
            variant='body1Semibold'
            sx={(theme) => ({
              m: theme.spacing(0),
              fontWeight: 600,
            })}
          >
            กรุณาตรวจสอบให้ช่วงค่า P ถูกต้อง ครบถ้วน ต่อเนื่องกัน ไม่ซ้อนทับหรือเว้นช่องว่าง
            อยู่ในช่วง 0.00–1.00 และปรับค่าเป็นขั้นละ 0.01
          </AlertTitle>
        </Alert>
      ) : null}
      <FormProvider {...formContext}>
        <Form
          onSubmit={formContext.handleSubmit(onSubmit)}
          sx={(theme) => ({
            p: theme.spacing(4),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(4),
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.shape.rounded(2),
          })}
        >
          <Text variant='body1Semibold'>กำหนดเกณฑ์ความยากใหม่ (P Buffer)</Text>
          <Flex sx={{ gap: (theme) => theme.spacing(6) }}>
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                gap: theme.spacing(3),
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                backgroundColor: theme.palette.grey[50],
                p: theme.spacing(3),
                alignItems: 'center',
                width: '100%',
              })}
            >
              <Flex sx={{ alignItems: 'center' }}>
                <CircleIcon
                  sx={(theme) => ({
                    width: '10px',
                    height: '10px',
                    mr: theme.spacing(2),
                    color: theme.palette.error.main,
                  })}
                />
                <Text>ยาก</Text>
              </Flex>
              <Flex sx={{ alignItems: 'flex-start', width: '100%' }}>
                <FormNumberInput
                  disabled
                  name=''
                  placeholder='0.00'
                  sx={{ '.MuiInputBase-input': { textAlign: 'center' }, width: '100%' }}
                  maxDecimalPlaces={2}
                />
                <Text sx={{ mx: (theme) => theme.spacing(2), p: (theme) => theme.spacing(2, 2) }}>
                  -
                </Text>
                <FormNumberInput
                  sx={{ '.MuiInputBase-input': { textAlign: 'center' }, width: '100%' }}
                  name='pBufferMaxHardThreshold'
                  max={1}
                  maxDecimalPlaces={2}
                />
              </Flex>
              <Text variant='body2' sx={{ color: (theme) => theme.palette.text.secondary }}>
                P Initial : 0 - 0.4
              </Text>
            </Flex>
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                gap: theme.spacing(3),
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                backgroundColor: theme.palette.grey[50],
                p: theme.spacing(3),
                alignItems: 'center',
                width: '100%',
              })}
            >
              <Flex sx={{ alignItems: 'center' }}>
                <CircleIcon
                  sx={(theme) => ({
                    width: '10px',
                    height: '10px',
                    mr: theme.spacing(2),
                    color: theme.palette.warning.main,
                  })}
                />
                <Text>กลาง</Text>
              </Flex>
              <Flex sx={{ alignItems: 'center', width: '100%' }}>
                <FormNumberInput
                  sx={{ '.MuiInputBase-input': { textAlign: 'center' }, width: '100%' }}
                  name='pBufferMinMediumThreshold'
                  max={1}
                  maxDecimalPlaces={2}
                />
                <Text sx={{ mx: (theme) => theme.spacing(2) }}>-</Text>
                <FormNumberInput
                  sx={{ '.MuiInputBase-input': { textAlign: 'center' }, width: '100%' }}
                  name='pBufferMaxMediumThreshold'
                  max={1}
                  maxDecimalPlaces={2}
                />
              </Flex>
              <Text variant='body2' sx={{ color: (theme) => theme.palette.text.secondary }}>
                P Initial : 0.41 - 0.7
              </Text>
            </Flex>

            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                gap: theme.spacing(3),
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.shape.rounded(2),
                backgroundColor: theme.palette.grey[50],
                p: theme.spacing(3),
                alignItems: 'center',
                width: '100%',
              })}
            >
              <Flex sx={{ alignItems: 'center' }}>
                <CircleIcon
                  sx={(theme) => ({
                    width: '10px',
                    height: '10px',
                    mr: theme.spacing(2),
                    color: theme.palette.success.main,
                  })}
                />
                <Text>ง่าย</Text>
              </Flex>
              <Flex sx={{ alignItems: 'center', width: '100%' }}>
                <FormNumberInput
                  sx={{ '.MuiInputBase-input': { textAlign: 'center' }, width: '100%' }}
                  name='pBufferMinEasyThreshold'
                  max={1}
                  maxDecimalPlaces={2}
                />
                <Text sx={{ mx: (theme) => theme.spacing(2) }}>-</Text>
                <FormNumberInput
                  sx={{ '.MuiInputBase-input': { textAlign: 'center' }, width: '100%' }}
                  name=''
                  placeholder='1.00'
                  disabled
                  maxDecimalPlaces={2}
                />
              </Flex>
              <Text variant='body2' sx={{ color: (theme) => theme.palette.text.secondary }}>
                P Initial : 0.71 - 1
              </Text>
            </Flex>
          </Flex>
          <Divider />
          <Flex sx={{ gap: (theme) => theme.spacing(2), justifyContent: 'end' }}>
            <Button color='secondary' variant='outlined' onClick={() => handleReset()}>
              Reset
            </Button>
            <Button type='submit'>Apply</Button>
          </Flex>
        </Form>
      </FormProvider>
    </Flex>
  );
}
